import {AccountType, ANONYMOUS_USER_TYPE_NAME, SubscriptionType, UserType} from "../components/constants/securspace-constants";
import type { Account } from "../types/Account";

/**
 * Helper type for functions that accept partial Account objects
 */
type AccountWithUserType = Pick<Account, 'userType'>;
type AccountWithType = Pick<Account, 'type'>;
type AccountWithTosContext = Pick<Account, 'type' | 'userType' | 'hasAcceptedCurrentTos'>;
type AccountWithRedirectContext = Pick<Account, 'type' | 'userType' | 'hasAcceptedCurrentTos' | 'subscriptionType'>;

/**
 * Check if user is logged in
 * @param user - Account object or null/undefined
 * @return true if user is logged in (i.e. userType is not ANONYMOUS_USER_TYPE_NAME), false otherwise
 */
const isLoggedIn = (user?: AccountWithUserType | null) => {
  return user !== null && user !== undefined && user.userType !== ANONYMOUS_USER_TYPE_NAME;
};

/**
 * Check if user has any of the specified user types
 */
const hasUserType = (user: AccountWithUserType | undefined | null, ...userTypeSets: string[][]): boolean => {
  return userTypeSets.reduce((accumulator, userTypeSet) => accumulator || userTypeSet.includes(user?.userType || ''), false);
}

const getSuperAdminUserTypes = (): string[] => {
  return [
    UserType.SUPER_ADMIN
  ];
}

const getAdminUserTypes = (): string[] => {
  return [
    UserType.ADMIN,
    UserType.SUPER_ADMIN
  ];
}

const getOwnerUserTypes = (): string[] => {
  return [
    UserType.OWNER,
    UserType.PRIMARY_OWNER
  ];
}

const getBuyerUserTypes = (): string[] => {
  return [
    UserType.OWNER,
    UserType.PRIMARY_OWNER,
    UserType.DISPATCHER
  ];
}

const getSupplierUserTypes = (): string[] => {
  return [
    UserType.OWNER,
    UserType.PRIMARY_OWNER,
    UserType.GATE_MANAGER,
    UserType.GATE_CLERK
  ];
}

const getGateManagerUserTypes = (): string[] => {
  return [
    UserType.GATE_MANAGER
  ];
}

const getGateOperatorUserTypes = (): string[] => {
  return [
    UserType.GATE_MANAGER,
    UserType.GATE_CLERK
  ];
}

const isBuyerAccountType = (accountType?: string): boolean => {
  return accountType === AccountType.BUYER;
}

const isSupplierAccountType = (accountType?: string): boolean => {
  return accountType === AccountType.SUPPLIER;
}

const isAdminAccountType = (accountType?: string): boolean => {
  return accountType === AccountType.ADMIN;
}

const isBuyerAccount = (user?: AccountWithType | null): boolean => {
  return isBuyerAccountType(user?.type);
}

const isSupplierAccount = (user?: AccountWithType | null): boolean => {
  return isSupplierAccountType(user?.type);
}

/**
 * Check if the logged in account type is admin.
 * This is useful for identifying if the logged-in or proxy logged-in user Account is an admin.
 * @param user - Account object or partial account with type property
 * @returns true if user is an admin account
 */
const isAdminAccount = (user?: AccountWithType | null): boolean => {
  return isAdminAccountType(user?.type);
}

const isGmsProAccount = (user?: Pick<Account, 'subscriptionType'> | null): boolean => {
  return user?.subscriptionType === SubscriptionType.GMS_PRO;
}

/**
 * Whether the supplier may manage capacity schedules (#3443): GMS PRO and enrolled in capacity
 * enforcement by an admin. Unenrolled suppliers' bookings are never held to capacity, so editing
 * capacity would have no effect on bookings.
 */
const canManageCapacitySchedule = (
  user?: Pick<Account, 'subscriptionType' | 'capacityEnforcementEnabled'> | null,
): boolean => {
  return isGmsProAccount(user) && !!user?.capacityEnforcementEnabled;
}

const hasSuperAdminAccess = (user?: AccountWithUserType | null): boolean => {
  return hasUserType(user, getSuperAdminUserTypes());
}

const hasAdminAccess = (user?: AccountWithUserType | null): boolean => {
  return hasUserType(user, getAdminUserTypes());
}

const hasPrimaryOwnerAccess = (user?: AccountWithUserType | null): boolean => {
  return (user?.userType === UserType.PRIMARY_OWNER) || hasAdminAccess(user);
}

const hasOwnerAccess = (user?: AccountWithUserType | null): boolean => {
  return hasUserType(user, getOwnerUserTypes(), getAdminUserTypes());
}

const hasGateManagerAccess = (user?: AccountWithUserType | null): boolean => {
  return hasUserType(user, getGateManagerUserTypes(), getOwnerUserTypes(), getAdminUserTypes());
}

const hasGateClerkAccess = (user?: AccountWithUserType | null): boolean => {
  return hasUserType(user, getGateOperatorUserTypes(), getOwnerUserTypes(), getAdminUserTypes());
}

const hasDispatcherAccess = (user?: AccountWithUserType | null): boolean => {
  return hasUserType(user, getBuyerUserTypes(), getAdminUserTypes());
}

const hasValidUserType = (user?: AccountWithUserType | null): boolean => {
  return !!user?.userType && (Object.values(UserType) as string[]).includes(user.userType);
}

const getLocationAllowsDailyBookings = (pricePerDay?: number): boolean => {
  return (pricePerDay || 0) > 0;
}

export {
  isLoggedIn,
  getSuperAdminUserTypes,
  getAdminUserTypes,
  getOwnerUserTypes,
  getBuyerUserTypes,
  getSupplierUserTypes,
  getGateManagerUserTypes,
  getGateOperatorUserTypes,
  getLocationAllowsDailyBookings,

  hasSuperAdminAccess,
  hasAdminAccess,
  hasPrimaryOwnerAccess,
  hasOwnerAccess,
  hasGateManagerAccess,
  hasGateClerkAccess,
  hasDispatcherAccess,
  hasValidUserType,

  isBuyerAccountType,
  isSupplierAccountType,
  isAdminAccountType,
  isBuyerAccount,
  isSupplierAccount,
  isAdminAccount,
  isGmsProAccount,
  canManageCapacitySchedule
};

export type { AccountWithUserType, AccountWithType, AccountWithTosContext, AccountWithRedirectContext };

